import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

type SelectedProductState = {
  balance_to_paid: number
  continueAsGuest: boolean
  currentAmountToCollect: number
  currentStep: CheckoutStep
  customer: Customer | null
  over_paid: number
  paymentCard?: PaymentMethodType | null
  payment_card_first_digits?: string | null
  payment_card_last_digits?: string | null
  payment_method: any
  payment_method_type?: string
  payment_methods?: any
  payments?: any[]
  payments_loading?: boolean
  totalPayable: number
  total_paid: number
  future_payment_date?: string | null
  custom_reference?: string | null
}

const initialState: SelectedProductState = {
  balance_to_paid: 0,
  continueAsGuest: false,
  currentAmountToCollect: 0,
  currentStep: 'ADD_PRODUCTS',
  customer: null,
  over_paid: 0,
  paymentCard: null,
  payment_card_first_digits: null,
  payment_card_last_digits: null,
  payment_method: null,
  payment_method_type: 'cash',
  payment_methods: null,
  payments: [],
  payments_loading: false,
  totalPayable: 0,
  total_paid: 0,
  future_payment_date: null,
  custom_reference: null,
}

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    resetCheckout() {
      return initialState
    },

    // set the payment methods
    setPaymentMethods(state, action: PayloadAction<any>) {
      state.payment_methods = action.payload
    },

    setValues(state, action: PayloadAction<Partial<SelectedProductState>>) {
      return { ...state, ...action.payload }
    },

    setCustomer(state, action: PayloadAction<Customer | null>) {
      if (action.payload?.id) {
        state.customer = action.payload
      } else {
        state.customer = null
      }
    },

    setContinueAsGuest(state, action: PayloadAction<boolean>) {
      state.customer = null
      state.continueAsGuest = action.payload
    },

    setPaymentMethod(state, action: PayloadAction<PaymentMethod>) {
      state.payment_method = action.payload
    },

    setPaymentMethodType(state, action: PayloadAction<PaymentMethod>) {
      state.payment_method_type = action.payload
    },

    setCurrentStep(state, action: PayloadAction<CheckoutStep>) {
      state.currentStep = action.payload
    },

    setAmountToCollect(state, action: PayloadAction<number>) {
      state.currentAmountToCollect = action.payload
    },

    setPaymentCard(state, action: PayloadAction<PaymentMethodType | null>) {
      state.paymentCard = action.payload
    },

    setFirstDigits(state, action: PayloadAction<string>) {
      state.payment_card_first_digits = action.payload
    },

    setLastDigits(state, action: PayloadAction<string>) {
      state.payment_card_last_digits = action.payload
    },

    setFuturePaymentDate(state, action: PayloadAction<string>) {
      state.future_payment_date = action.payload
    },

    setCustomReference(state, action: PayloadAction<string>) {
      state.custom_reference = action.payload
    },

    setPayments(state, action: PayloadAction<any>) {
      state.payments = action.payload
    },

    setTotalPayment(state, action: PayloadAction<number>) {
      state.total_paid = action.payload
    },

    setBalanceToPay(state, action: PayloadAction<number>) {
      state.balance_to_paid = action.payload
    },

    setOverPaid(state, action: PayloadAction<number>) {
      state.over_paid = action.payload
    },

    setPaymentsLoading(state, action: PayloadAction<boolean>) {
      state.payments_loading = action.payload
    },
  },
})

export const {
  resetCheckout,
  setAmountToCollect,
  setBalanceToPay,
  setContinueAsGuest,
  setCurrentStep,
  setCustomer,
  setFirstDigits,
  setLastDigits,
  setOverPaid,
  setPaymentCard,
  setPaymentMethod,
  setPaymentMethodType,
  setPaymentMethods,
  setPayments,
  setPaymentsLoading,
  setTotalPayment,
  setValues,
  setFuturePaymentDate,
  setCustomReference,
} = checkoutSlice.actions

export default checkoutSlice.reducer
